import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

import amplitude from 'amplitude-js';
import { toggleModalWithTimer, toggleSpinner } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { setFieldError } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import AmountVerificationInput from 'apps/auth/components/antifraud/AmountVerificationInput';
import ConfirmAmountCurrency from 'apps/auth/components/cards/confirm/ConfirmAmountCurrency';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { Row } from 'apps/auth/components/common/Layout/Row';
import Form from 'apps/auth/components/form-multi/Form';
import BasePage from 'apps/auth/components/utils/BasePage';
import Spinner from 'apps/auth/components/utils/Spinner/Spinner';
import {
    AF_VER_PAYMENT_CARD_ENTERED_VER_AMOUNT_CONT_CLICKED,
    AF_VERIFYING_PAYMENT_CARD_AMOUNT_CONFIRMATION_COMPLETED,
} from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { SCENARIO_ID } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import {
    FRAUD_FAILED_SUM_VERIFICATION,
    FRAUD_INCORRECT_AMOUNT,
    FRAUD_INTRO_SUM_VERIFICATION,
    FRAUD_PENDING,
    FRAUD_SUM_TRY_AGAIN,
} from 'apps/auth/constants/ComponentNames';
import { VERIFICATION_AMOUNT } from 'apps/auth/constants/fieldsName';
import { ANTIFRAUD_SUM_VERIFICATION } from 'apps/auth/constants/formName';
import { AUTH_ANTIFRAUD_AMOUNT_FIELD_ACTION, AUTH_SEND_MAIN_LINK } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { useExtra, useForm, usePageMessages } from 'apps/auth/hooks/selectors';

const getFirstScreen = (extra) => {
    if (extra.inPending) {
        return FRAUD_PENDING;
    } else if (extra.failedCheck) {
        return FRAUD_FAILED_SUM_VERIFICATION;
    }
    return FRAUD_INTRO_SUM_VERIFICATION;
};

const getScreenAfterWaiting = (extra) => {
    if (extra.failedCheck) {
        return FRAUD_FAILED_SUM_VERIFICATION;
    }
    return FRAUD_INTRO_SUM_VERIFICATION;
};

const AntiFraudCheckSumCard = (props) => {
    const messages = usePageMessages();
    const extra = useExtra();
    const form = useForm(ANTIFRAUD_SUM_VERIFICATION);
    const verificationAmountField = form?.fields?.[VERIFICATION_AMOUNT];
    const [currentScreen, setCurrentScreen] = useState(getFirstScreen(extra));
    const [loading, setLoading] = useState(true);
    const { payId } = useParams();
    const isDisabledButton = verificationAmountField?.isError || !verificationAmountField?.value;

    useEffect(() => {
        if (extra?.inPending === false) {
            setLoading(false);
            props.toggleSpinner(false);
            return;
        }

        let attempt = 1;
        let intervalId;

        if (loading) {
            props.toggleSpinner(true);
            intervalId = setInterval(() => {
                const isLastAttempt = attempt >= extra?.numberOfAttempts;
                apiFetchData(`/auth/antifraud/check/sum/verification/${payId}${isLastAttempt ? '?lastAttemptToWaitPayment=true' : ''}`)
                    .then((json) => {
                        if (attempt++ >= extra?.numberOfAttempts || json.extra?.inPending === false) {
                            setLoading(false);
                            props.toggleSpinner(false);
                            clearInterval(intervalId);
                            props.addExtras(json.extra);
                            props.updatePageMessages(json.messages);

                            setCurrentScreen(getScreenAfterWaiting(json.extra));
                        }
                    })
                    .catch((e) => {
                        console.warn(e);
                        setLoading(false);
                        props.toggleSpinner(false);
                        clearInterval(intervalId);
                    });
            }, extra.setRefreshTimer);
        }

        return () => clearInterval(intervalId);
    }, [loading]);

    const submitHandler = (fields, json) => {
        let errorCode = json?.extra?.errorCode;
        if (errorCode) {
            if (errorCode === 43) {
                setCurrentScreen(FRAUD_INCORRECT_AMOUNT);
            } else if (errorCode === 40) {
                setCurrentScreen(FRAUD_SUM_TRY_AGAIN);
            } else {
                window.location.href = AUTH_SEND_MAIN_LINK;
            }
        } else {
            props.toggleModalWithTimer(true, messages.cardVerifiedSpinner, () => {
                amplitude.getInstance().logEvent(AF_VERIFYING_PAYMENT_CARD_AMOUNT_CONFIRMATION_COMPLETED, { [SCENARIO_ID]: json.extra?.fraudScenarioId });
                window.location.href = json.extra?.redirectUrl;
            });
        }
    };

    const componentMap = {
        [FRAUD_PENDING]: <Spinner />,
        [FRAUD_INTRO_SUM_VERIFICATION]: (
            <CommonPage backUrl={AUTH_SEND_MAIN_LINK} leftCloseIcon>
                <HeaderPanel title={messages.enterAmountTitle} subtitle={messages.enterAmountSubTitle} />
                <Form
                    id="antifraud_amount_field"
                    formName={form?.name}
                    fields={form.fields}
                    onSubmitHandler={submitHandler}
                    action={AUTH_ANTIFRAUD_AMOUNT_FIELD_ACTION + `${extra.objectId}/${extra.confirmType}/${extra.payId}`}
                >
                    {Object.values(form.fields).map((field) => {
                        return (
                            <Row indent="24px" key={field.name}>
                                <AmountVerificationInput
                                    minAmount={extra.minAmount}
                                    maxAmount={extra.maxAmount}
                                    formName={form.name}
                                    field={field}
                                    isNewInput
                                    validateOnBlur={false}
                                >
                                    <ConfirmAmountCurrency currencyIcon={''} currencyCode={''} />
                                </AmountVerificationInput>
                            </Row>
                        );
                    })}
                    <FixedBottomPanel isRightAligned>
                        <Button
                            type="submit"
                            raised={true}
                            caption={messages.buttonContinue}
                            onClick={() => {
                                amplitude.getInstance().logEvent(AF_VER_PAYMENT_CARD_ENTERED_VER_AMOUNT_CONT_CLICKED, { [SCENARIO_ID]: extra.fraudScenarioId });
                            }}
                            disabled={isDisabledButton}
                        />
                    </FixedBottomPanel>
                </Form>
            </CommonPage>
        ),
        [FRAUD_FAILED_SUM_VERIFICATION]: (
            <InfoPage
                animatedIconFail
                headerText={messages.paymentFailedText}
                secondaryText={messages.paymentFailedSubText}
                primaryButtonCaption={messages.tryAgainButton}
                primaryButtonType="link"
                isTagA
                leftCloseIcon
                backUrl={AUTH_SEND_MAIN_LINK}
                primaryButtonLink={`/auth/antifraud/check/cvv/${extra.payId}`}
            />
        ),
        [FRAUD_INCORRECT_AMOUNT]: (
            <InfoPage
                animatedIconFail
                headerText={messages.errorIncorrectAmount}
                primaryButtonCaption={messages.tryAgainButton}
                primaryButtonType="link"
                primaryButtonLink={`/auth/antifraud/check/cvv/${extra.payId}`}
                primaryButtonOnClick={(e) => {
                    e.preventDefault();
                    setCurrentScreen(FRAUD_INTRO_SUM_VERIFICATION);
                }}
            />
        ),
        [FRAUD_SUM_TRY_AGAIN]: (
            <InfoPage
                iconName="card-alert-80"
                iconColor="primary"
                headerText={messages.needNewTryTitle}
                secondaryText={<DangerouslyHtml dangerouslyHtml={messages.needNewTryText} />}
                buttonRowCaption={messages.contactSupport}
                buttonRowOnClick={() => props.toggleChat(true)}
                primaryButtonCaption={messages.verifyAgain}
                primaryButtonType="link"
                primaryButtonLink={`/auth/antifraud/check/cvv/${extra.payId}`}
            />
        ),
    };
    return <>{currentScreen ? componentMap[currentScreen] : null}</>;
};
export default connect(null, {
    toggleModalWithTimer,
    toggleChat,
    toggleSpinner,
    updatePageMessages,
    addExtras,
    setFieldError,
})(BasePage(AntiFraudCheckSumCard));
